import { CanceledError } from 'axios';
import { config } from 'shared/config';

/**
 * Reads that outlive their request (DP-031). A read still running after the
 * server's sync window answers `202 {readJob}`; the browser then follows the
 * job until it gives the read's own final response. Leaving (abort) cancels it.
 */
/** A read job as the server reports it: rows read and, once every source is counted, their total. */
export type ReadJob = { id: string; phase?: string; rows?: number; total?: number | null; elapsedMs?: number; receivedAt?: number };
export type ReadJobOutcome = { status: number; data: any };
/** The signed-in session's fetch (`session.authenticatedFetch`). */
export type ReadJobSend = (url: string, init?: RequestInit) => Promise<Response>;

const POLL_MS = 1500;
// The server's message for a read its user cancelled (read_control.MESSAGES['cancelled']).
const READ_CANCELLED_MESSAGE = 'The read was cancelled.';
export const isCancelledRead = (message?: string | null): boolean => Boolean(message?.includes(READ_CANCELLED_MESSAGE));
const running = new Map<string, ReadJob>();
const cancels = new Map<string, () => void>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(listener => listener());

export const isReadJobResponse = (status: number, data: any): data is { readJob: ReadJob } =>
    status === 202 && Boolean(data?.readJob?.id);

/** Subscribe to progress changes of the reads followed under a key (a visual id). */
export const subscribeReadJobs = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};
export const readJobProgress = (key: string): ReadJob | undefined => running.get(key);

const jobPath = (id: string) => `${config.API_URL}/api/read-jobs/${encodeURIComponent(id)}`;

/** Stop the read followed under `key`; its request then ends with the server's "cancelled" answer. */
export const cancelReadJob = (key: string): void => {
    cancels.get(key)?.();
};

const pause = (ms: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
    if (signal?.aborted) { reject(new CanceledError('This read is no longer needed.')); return; }
    const done = () => { signal?.removeEventListener('abort', stop); resolve(); };
    const timer = window.setTimeout(done, ms);
    const stop = () => { window.clearTimeout(timer); reject(new CanceledError('This read is no longer needed.')); };
    signal?.addEventListener('abort', stop, { once: true });
});

export async function followReadJob(job: ReadJob, options: { send: ReadJobSend; signal?: AbortSignal; key?: string }): Promise<ReadJobOutcome> {
    const { send, signal, key } = options;
    const requestCancel = () => { void send(jobPath(job.id) + '/cancel', { method: 'POST' }).catch(() => undefined); };
    const show = (state: ReadJob) => { if (key) { running.set(key, { ...state, receivedAt: Date.now() }); emit(); } };
    if (key) cancels.set(key, requestCancel);
    show(job);
    try {
        for (;;) {
            await pause(POLL_MS, signal);
            const response = await send(jobPath(job.id), { signal });
            const data = await response.json().catch(() => ({ error: response.statusText }));
            if (isReadJobResponse(response.status, data)) { show(data.readJob); continue; }
            return { status: response.status, data };
        }
    } catch (error) {
        if (signal?.aborted) requestCancel();
        throw error;
    } finally {
        if (key && running.get(key)?.id === job.id) { running.delete(key); cancels.delete(key); emit(); }
    }
}
